import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, ExternalLink } from 'lucide-react'
import type { ScheduleItem } from '@/api/client'
import { useSchedule } from '@/lib/queries'
import { parseTime } from '@/lib/time'
import { Button, DataTable, EmptyState, PlatformMark, When, platformName, type Column } from '@/ui'
import { engagementText } from '../results/Results'
import { channelLabel, headline, refOf, useOpenDetail } from '../schedule/shared'
import { Section } from './shared'

/** How far back the section reads, and how many rows it shows before 더 보기. */
const DAYS = 90
const PAGE = 10
const WEEK_MS = 7 * 86_400_000

/** The reactions a row always shows; the rest only when they are not zero.
 *  "리포스트 0 · 인용 0" on every row said nothing and pushed the line to two. */
const ALWAYS = ['views', 'likes', 'replies', 'comments']

/** A post's last read reaction in one short line: "조회 1,790 · 좋아요 31 · 답글 1". */
export function reactionText(e: ScheduleItem['engagement']): string {
  if (!e) return ''
  return engagementText(Object.fromEntries(Object.entries(e).filter(([k, v]) => ALWAYS.includes(k) || typeof v !== 'number' || v !== 0)))
}

const num = (it: ScheduleItem, key: string) => {
  const v = it.engagement?.[key]
  return typeof v === 'number' ? v : 0
}

/** 발행한 글 — what this campaign put up, newest first, with the reaction last
 *  read and the link to the post, over the last week's totals. It was
 *  reachable only through 결과 with the campaign filter; a campaign's page is
 *  where the operator looks first. A row opens the post's detail, as it does
 *  in 결과. */
export function CampaignPosts({ campaignId, now: nowProp }: { campaignId: number; now?: Date }) {
  const now = useMemo(() => nowProp ?? new Date(), [nowProp])
  const from = useMemo(() => new Date(now.getFullYear(), now.getMonth(), now.getDate() - (DAYS - 1)).toISOString(), [now])
  const feed = useSchedule({ status: 'published', source: 'campaign', campaignId, from })
  const open = useOpenDetail()
  const [shown, setShown] = useState(PAGE)
  const items = useMemo(
    () => (feed.data?.items ?? []).slice().sort((a, b) => String(b.published_at).localeCompare(String(a.published_at))),
    [feed.data],
  )
  const week = useMemo(() => {
    const since = now.getTime() - WEEK_MS
    const recent = items.filter((it) => (parseTime(it.published_at)?.getTime() ?? 0) >= since)
    const sum = (key: string) => recent.reduce((a, it) => a + num(it, key), 0)
    return { posts: recent.length, views: sum('views'), likes: sum('likes'), replies: sum('replies') + sum('comments') }
  }, [items, now])

  const columns: Column<ScheduleItem>[] = [
    {
      key: 'at',
      header: '게시',
      card: 'meta',
      className: 'w-28 whitespace-nowrap text-muted-foreground',
      cell: (it) => <When iso={it.published_at} />,
    },
    {
      key: 'where',
      header: '계정',
      card: 'sub',
      className: 'whitespace-nowrap',
      cell: (it) => (
        <span className="inline-flex items-center gap-1.5">
          <PlatformMark platform={it.platform} />
          {channelLabel(it)}
        </span>
      ),
    },
    {
      key: 'title',
      header: '글',
      card: 'title',
      cell: (it) => <span className="line-clamp-2 break-words">{headline(it, 80)}</span>,
    },
    {
      key: 'engagement',
      header: '반응',
      className: 'tabular-nums md:whitespace-nowrap',
      cardHide: (it) => !reactionText(it.engagement),
      cell: (it) => {
        const text = reactionText(it.engagement)
        return text ? (
          <span title={engagementText(it.engagement) + (it.engagement?.t ? ` · 읽은 때 ${it.engagement.t}` : '')}>{text}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )
      },
    },
    {
      key: 'link',
      header: '링크',
      card: 'meta',
      align: 'center',
      width: '56px',
      cell: (it) =>
        it.external_url ? (
          <a
            href={it.external_url}
            target="_blank"
            rel="noreferrer"
            aria-label={`${platformName(it.platform)}에서 글 열기`}
            title={it.external_url}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-accent hover:bg-muted"
          >
            <ExternalLink size={16} aria-hidden />
          </a>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
  ]

  return (
    <Section
      title="발행한 글"
      aside={items.length > 0 ? `최근 ${DAYS}일 ${items.length}건` : undefined}
      label="발행한 글"
      actions={
        <Link to={`/results?campaign_id=${campaignId}`} className="inline-flex items-center gap-1 text-meta font-medium text-accent hover:underline">
          결과에서 기간별로 보기
          <ArrowRight size={12} aria-hidden />
        </Link>
      }
    >
      {items.length > 0 && (
        <p className="text-table tabular-nums text-muted-foreground" data-testid="week">
          최근 7일 <b className="font-medium text-foreground">{week.posts}</b>건
          {week.posts > 0 && (
            <>
              {' '}· 조회 <b className="font-medium text-foreground">{week.views.toLocaleString()}</b> · 좋아요{' '}
              <b className="font-medium text-foreground">{week.likes.toLocaleString()}</b> · 답글{' '}
              <b className="font-medium text-foreground">{week.replies.toLocaleString()}</b>
            </>
          )}
        </p>
      )}
      <DataTable
        label="발행한 글"
        columns={columns}
        rows={feed.data ? items.slice(0, shown) : undefined}
        loading={feed.isPending}
        error={feed.error}
        onRetry={() => feed.refetch()}
        rowKey={(it) => `${it.kind}-${it.id}`}
        onRowClick={(it) => open(refOf(it))}
        empty={<EmptyState className="py-6" title="아직 올라간 글이 없다" description={`최근 ${DAYS}일 동안 이 캠페인에서 게시된 글이 없다.`} />}
      />
      {items.length > shown && (
        <Button size="sm" onClick={() => setShown((n) => n + PAGE)}>
          더 보기 ({items.length - shown}건 남음)
        </Button>
      )}
    </Section>
  )
}
